import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RouterRow {
  readonly routerId: string;
  readonly name: string;
  readonly sourceType: string;
  readonly targetType: string;
  readonly status: string;
  readonly lastRoutedAt: string | null;
}

/**
 * GR L28.06 — App Data Routing (`/lcnc/apps/{id}/data`). Lists the
 * `lcnc_data_routers` bound to this application — where each form submission is
 * persisted or forwarded — using the GR net-new data-router backend so the
 * builder can see the app's data plumbing.
 */
export function GrAppDataPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [rows, setRows] = useState<RouterRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: RouterRow[] }>(`/v1/lcnc/data-routers?applicationId=${id}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  return (
    <main aria-label="lcnc-data">
      <h1>{t('gr28.appData.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr28.appData.loading')}</p> : (
        <>
          <p aria-label="router-count">{t('gr28.appData.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr28.appData.empty')}</p> : (
            <table aria-label="router-table">
              <thead><tr><th>{t('gr28.appData.colName')}</th><th>{t('gr28.appData.colSource')}</th><th>{t('gr28.appData.colTarget')}</th><th>{t('gr28.appData.colStatus')}</th><th>{t('gr28.appData.colLastRouted')}</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.routerId}>
                    <td>{r.name}</td><td>{r.sourceType}</td><td>{r.targetType}</td>
                    <td>{r.status}</td><td>{at(r.lastRoutedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
